/** @format */

import React from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	Chip,
	Avatar,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { colors } from "../../utility/types/constant";
import { formatCurrency } from "../../utility/currencyUtils";

interface MissedContribution {
	unionId: string;
	circleName: string;
	circleImage?: string;
	contributionAmount: number;
	missedCount: number;
	totalDueCents: number;
	totalDueDollars: number;
	contributionDays: string[];
	isContributionDayToday: boolean;
	isTodayAlreadyPaid?: boolean; // True if today is a contribution day and has already been paid
	cycleStartDate: string;
	cycleEndDate: string;
	hasAutoPayEnabled: boolean;
	unpaidDates?: string[]; // Array of YYYY-MM-DD dates that were missed
}

interface MissedContributionsModalProps {
	isOpen: boolean;
	onClose: () => void;
	missedContributions: MissedContribution[];
	onContributeClick: (unionId: string) => void;
}

export const MissedContributionsModal: React.FC<
	MissedContributionsModalProps
> = ({ isOpen, onClose, missedContributions, onContributeClick }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const totalMissedAcrossAllCircles = missedContributions.reduce(
		(sum, mc) => sum + mc.missedCount,
		0,
	);
	const totalAmountDue = missedContributions.reduce(
		(sum, mc) => sum + mc.totalDueDollars,
		0,
	);

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			fullScreen={isMobile}
			PaperProps={{
				sx: {
					maxHeight: isMobile ? "100vh" : "80vh",
					borderRadius: isMobile ? 0 : 2,
					m: isMobile ? 0 : undefined,
				},
			}}>
			<DialogTitle sx={{ px: isMobile ? 2 : 3, py: isMobile ? 1.5 : 2 }}>
				<Box display="flex" alignItems="center" gap={1}>
					<span style={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>⚠️</span>
					<Typography
						variant={isMobile ? "subtitle1" : "h6"}
						component="span"
						sx={{ fontWeight: 600 }}>
						Missed Contributions
					</Typography>
				</Box>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 0.75, fontSize: isMobile ? "0.8rem" : undefined }}>
					You have {totalMissedAcrossAllCircles} unpaid contribution
					{totalMissedAcrossAllCircles !== 1 ? "s" : ""} totaling{" "}
					{formatCurrency(totalAmountDue)}.
				</Typography>
				<Box
					sx={{
						mt: 1,
						p: isMobile ? 1 : 1.5,
						bgcolor: "#fef2f2",
						borderRadius: 1,
						border: "1px solid #fecaca",
					}}>
					<Typography
						variant="body2"
						sx={{
							color: "#991b1b",
							fontWeight: 500,
							fontSize: isMobile ? "0.78rem" : undefined,
						}}>
						🚫 You cannot make new contributions until you pay your missed
						contributions.
					</Typography>
				</Box>
			</DialogTitle>

			<DialogContent
				dividers
				sx={{ px: isMobile ? 1.5 : 3, py: isMobile ? 1.5 : 2 }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: isMobile ? 1.5 : 2,
					}}>
					{missedContributions.map((mc) => (
						<Box
							key={mc.unionId}
							sx={{
								border: "1px solid",
								borderColor: "divider",
								borderRadius: 2,
								p: isMobile ? 1.5 : 2,
								background: "linear-gradient(to right, #fff7ed, #fefce8)",
							}}>
							<Box
								display="flex"
								flexDirection={isMobile ? "column" : "row"}
								alignItems={isMobile ? "center" : "flex-start"}
								gap={isMobile ? 1.5 : 2}>
								{/* Circle Image */}
								{mc.circleImage && (
									<Avatar
										src={mc.circleImage}
										alt={mc.circleName}
										sx={{
											width: isMobile ? 48 : 64,
											height: isMobile ? 48 : 64,
											flexShrink: 0,
										}}
									/>
								)}

								<Box flex={1} sx={{ width: "100%", minWidth: 0 }}>
									{/* Circle Name */}
									<Typography
										variant={isMobile ? "subtitle1" : "h6"}
										gutterBottom
										sx={{
											fontWeight: 600,
											textAlign: isMobile ? "center" : "left",
											wordBreak: "break-word",
										}}>
										{mc.circleName}
									</Typography>

									{/* Missed Count */}
									<Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
										<span
											style={{
												fontSize: isMobile ? "0.875rem" : "1rem",
												flexShrink: 0,
											}}>
											⏱️
										</span>
										<Typography
											variant="body2"
											sx={{ fontSize: isMobile ? "0.8rem" : undefined }}>
											<strong>{mc.missedCount}</strong> missed contribution
											{mc.missedCount !== 1 ? "s" : ""}
										</Typography>
									</Box>

									{/* Total Due */}
									<Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
										<span
											style={{
												fontSize: isMobile ? "0.875rem" : "1rem",
												flexShrink: 0,
											}}>
											💵
										</span>
										<Typography
											variant="body2"
											sx={{ fontSize: isMobile ? "0.8rem" : undefined }}>
											Total due:{" "}
											<strong
												style={{
													fontSize: isMobile ? "1rem" : "1.125rem",
													color: "#ea580c",
												}}>
												{formatCurrency(mc.totalDueDollars)}
											</strong>
										</Typography>
									</Box>

									{/* Contribution Days */}
									<Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
										<span
											style={{
												fontSize: isMobile ? "0.875rem" : "1rem",
												flexShrink: 0,
											}}>
											📅
										</span>
										<Typography
											variant="body2"
											sx={{ fontSize: isMobile ? "0.8rem" : undefined }}>
											Contribution days: {mc.contributionDays.join(", ")}
										</Typography>
									</Box>

									{/* Today's contribution included notice */}
									{mc.isContributionDayToday &&
										(() => {
											// Check if today's date is actually in the unpaidDates array
											// Use local timezone to match user's perspective
											const today = new Date();
											const todayDateStr = `${today.getFullYear()}-${String(
												today.getMonth() + 1,
											).padStart(2, "0")}-${String(today.getDate()).padStart(
												2,
												"0",
											)}`;
											const todayIncluded =
												mc.unpaidDates?.includes(todayDateStr) ?? false;

											// Use backend flag if available, otherwise infer from unpaidDates
											const isTodayPaid =
												mc.isTodayAlreadyPaid ??
												(!todayIncluded && mc.isContributionDayToday);

											// If today is already paid, show green success message instead of warning
											if (isTodayPaid && !todayIncluded) {
												return (
													<Box
														sx={{
															mt: 0.75,
															mb: 0.75,
															p: isMobile ? 1 : 1.5,
															bgcolor: "#dcfce7",
															borderRadius: 1,
															border: "1px solid #86efac",
														}}>
														<Typography
															variant="body2"
															sx={{
																color: "#166534",
																fontWeight: 500,
																mb: 0.5,
																fontSize: isMobile ? "0.78rem" : undefined,
															}}>
															✅ Today (
															{new Date().toLocaleDateString("en-US", {
																weekday: "long",
																month: "short",
																day: "numeric",
															})}
															) is already paid!
														</Typography>
														<Typography
															variant="body2"
															sx={{
																color: "#166534",
																fontSize: isMobile ? "0.78rem" : undefined,
															}}>
															After paying your missed contributions, you'll be
															fully caught up.
														</Typography>
													</Box>
												);
											}

											return (
												<Box
													sx={{
														mt: 0.75,
														mb: 0.75,
														p: isMobile ? 1 : 1.5,
														bgcolor: todayIncluded ? "#dbeafe" : "#fef3c7",
														borderRadius: 1,
														border: `1px solid ${todayIncluded ? "#93c5fd" : "#fcd34d"
															}`,
													}}>
													<Typography
														variant="body2"
														sx={{
															color: todayIncluded ? "#1e40af" : "#92400e",
															fontWeight: 500,
															mb: 0.5,
															fontSize: isMobile ? "0.78rem" : undefined,
														}}>
														📌 Today (
														{new Date().toLocaleDateString("en-US", {
															weekday: "long",
															month: "short",
															day: "numeric",
														})}
														) is a contribution day.
													</Typography>
													<Typography
														variant="body2"
														sx={{
															color: todayIncluded ? "#1e40af" : "#92400e",
															fontSize: isMobile ? "0.78rem" : undefined,
														}}>
														{todayIncluded ?
															<>
																This payment includes your{" "}
																<strong>past missed contributions</strong>{" "}
																<strong>plus today's contribution</strong>, so
																you'll be fully caught up after payment.
															</>
															: <>
																After paying your missed contributions, you'll
																need to make{" "}
																<strong>today's contribution separately</strong>{" "}
																to be fully caught up.
															</>
														}
													</Typography>
												</Box>
											);
										})()}

									{/* Missed Dates */}
									{mc.unpaidDates && mc.unpaidDates.length > 0 && (
										<Box mb={1}>
											<Typography
												variant="caption"
												display="block"
												gutterBottom
												sx={{ fontWeight: 600, color: "text.secondary" }}>
												Missed dates:
											</Typography>
											<Box
												sx={{
													display: "flex",
													flexWrap: "wrap",
													gap: 0.5,
													maxHeight: isMobile ? "90px" : "120px",
													overflowY: "auto",
													p: isMobile ? 0.75 : 1,
													bgcolor: "#fff1f2",
													borderRadius: 1,
													WebkitOverflowScrolling: "touch",
												}}>
												{mc.unpaidDates.map((date) => (
													<Chip
														key={date}
														label={new Date(
															date + "T12:00:00",
														).toLocaleDateString("en-US", {
															month: "short",
															day: "numeric",
															year: "numeric",
														})}
														size="small"
														sx={{
															bgcolor: "#fecaca",
															color: "#991b1b",
															fontSize: isMobile ? "0.65rem" : "0.7rem",
															height: isMobile ? 24 : undefined,
														}}
													/>
												))}
											</Box>
										</Box>
									)}
									{/* Auto-pay Status */}
									{mc.hasAutoPayEnabled ?
										<Chip
											label={
												"✓ Auto-pay enabled" +
												(mc.isContributionDayToday ?
													" - Will charge automatically today"
													: "")
											}
											color="success"
											size="small"
											sx={{
												mb: 1,
												fontSize: isMobile ? "0.7rem" : undefined,
												height: "auto",
												"& .MuiChip-label": {
													whiteSpace: "normal",
													py: 0.5,
												},
											}}
										/>
										: <Chip
											label="! Auto-pay not enabled - Manual payment required"
											color="warning"
											size="small"
											sx={{
												mb: 1,
												fontSize: isMobile ? "0.7rem" : undefined,
												height: "auto",
												"& .MuiChip-label": {
													whiteSpace: "normal",
													py: 0.5,
												},
											}}
										/>
									}

									{/* Breakdown */}
									<Box
										sx={{
											fontSize: isMobile ? "0.7rem" : "0.75rem",
											color: "text.secondary",
											bgcolor: "background.paper",
											p: isMobile ? 0.75 : 1,
											borderRadius: 1,
											mb: 1,
										}}>
										<strong>Breakdown:</strong> {mc.missedCount} contribution
										{mc.missedCount !== 1 ? "s" : ""} ×{" "}
										{formatCurrency(mc.contributionAmount)} ={" "}
										{formatCurrency(mc.totalDueDollars)}
									</Box>

									{/* Action Button - Always show for missed contributions */}
									<Button
										onClick={() => onContributeClick(mc.unionId)}
										variant="contained"
										fullWidth
										sx={{
											bgcolor: "#dc2626",
											"&:hover": { bgcolor: "#b91c1c" },
											fontWeight: 600,
											fontSize: isMobile ? "0.8rem" : undefined,
											py: isMobile ? 1 : undefined,
											minHeight: 44, // Touch-friendly minimum
										}}>
										💳 Pay Missed Contributions -{" "}
										{formatCurrency(mc.totalDueDollars)}
									</Button>
								</Box>
							</Box>
						</Box>
					))}
				</Box>

				{/* Good to Know Section */}
				<Box
					sx={{
						mt: isMobile ? 2 : 3,
						p: isMobile ? 1.5 : 2,
						bgcolor: "#eff6ff",
						borderRadius: 2,
					}}>
					<Typography
						variant="subtitle2"
						gutterBottom
						sx={{ fontSize: isMobile ? "0.8rem" : undefined }}>
						💡 Good to Know:
					</Typography>
					<Box
						component="ul"
						sx={{
							fontSize: isMobile ? "0.7rem" : "0.75rem",
							pl: isMobile ? 1.5 : 2,
							m: 0,
							"& li": { mb: 0.5 },
						}}>
						<li>
							🚫 You must pay all missed contributions before making new
							contributions
						</li>
						<li>
							📅 When you pay, all missed contributions will be included in one
							payment
						</li>
						<li>
							✅ Once paid, your contribute button will be re-enabled for
							current day contributions
						</li>
					</Box>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: isMobile ? 2 : 3, py: isMobile ? 1.5 : 1 }}>
				<Button
					onClick={onClose}
					variant="outlined"
					fullWidth={isMobile}
					sx={{ minHeight: 44 }}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};
